import React, { useState, useEffect, useMemo } from "react";
import config from "../../config";
import api from "../../services/api";
import { Toast } from "../../shared/Toast";
import Paginator from '../../shared/Paginator';
import ScrollContainer from "react-indiana-drag-scroll";
import NoDataFound from '../../shared/NoDataFound';
import { useTableState } from "./utils/useTableState"
import { alphaformatDateTime } from "../../utils/dateFormat"
import TableLoader from "../../shared/TableLoader"
import FilterBar from './components/FilterBar';
import ContentWriterAssignmentModal from './components/ContentWriterAssignmentModal';
import ContentDrawer from "./components/ContentDrawer";
import LocationDetails from "./components/LocationDetails";

const STATUS_OPTIONS = [
    { value: 2, label: 'Saved' },
];
export const types = [
  {
    value: "all",
    label: "All",
  },
  {
    value: "full_page",
    label: "Full Image",
  },
  {
    value: "full_video",
    label: "Full Video",
  },
  {
      value:"half_video",
      label:"Half Video"
  },
  {
    value: "promoted_image_story",
    label: "Promoted Image Story ",
  },
  {
    value:"promoted_video_story",
    label:"Promoted Video Story"
  },
  {
    value: "classifiedads",
    label: "Classified Ads"
  }
]
const AssignToDesignerAds = () => {
  const [Designer, setDesigners] = useState([]);
  const [assignAds, setAssignAds] = useState([]);
  const [loader, setloader] = useState(false);
  const [designerAssignments, setDesignerAssignments] = useState({});
  const [totalPages, settotalPages] = useState(0);
  const { currentPage, setCurrentPage, handlePageClick, contentWriters, fetchContentWriters } = useTableState();
  const [showAdDetails, setShowAdDetails] = useState(false);
  const [rowData,setRowData]=useState({})
  // Content writer selection per creative and pick flow modal
  const [contentWriterAssignments, setContentWriterAssignments] = useState({});
  const [pickModalOpen, setPickModalOpen] = useState(false);
  const [pickModalContext, setPickModalContext] = useState({ creative_id: null, content_id: null, cw: null, designer: null });

  // Filter state
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [status, setStatus] = useState(STATUS_OPTIONS[0]); // Always 2 (Saved)
  const [adType, setAdType] = useState(null);
  const [search, setSearch] = useState('');

  const AssignToDesignData = async (page) => {
    try {
      setloader(true);
      let res = await api({
        url: `/alpha_cst_designer/getDesignes`,
        method: "POST",
        responseType: "json",
        data: {
          page,
          limit: config.PER_PAGE,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          status: status ? status.value : 2,
          adType: adType && adType.value !== 'all' ? adType.value : null,
          search: search || null,
        }
      });
      if (res.code === 200) {
        setAssignAds(res.data?.data);
        settotalPages(Math.ceil(res.data?.total / 10));
      }
      setloader(false);
    } catch (error) {
      setloader(false);
      console.error(error);
    }
  }

  const FetchDesigners = async () => {
    try {
      let res = await api({
        url: `/alpha_cst_designer/getDesigners`,
        method: "POST",
        responseType: "json",
        data: {}
      });
      if (res.code === 200)
        setDesigners(res.data?.data);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    FetchDesigners();
    fetchContentWriters();
    setCurrentPage(0);
  }, []);

  useEffect(() => {
    AssignToDesignData(currentPage);
  }, [currentPage, dateRange, status,adType,search]);

  const handleDateChange = (val) => setDateRange(val);
  const handleStatusChange = (val) => setStatus(val);
  const handleAdTypeChange = (val) => setAdType(val);
  const handleSearchChange = (val) => setSearch(val);

  const handlePick = async ({ creative_id, designer_id, designer_name, content_id = null }) => {
    try {
      if (!designer_id) {
        Toast('warning', "Please select a designer.");
        return;
      }

      let res = await api({
        url: `/alpha_cst_designer/assigntoDesigner`,
        method: "POST",
        responseType: "json",
        data: {
          creative_id,
          designer_id,
          designer_name,
        }
      });
      if (res.code === 200) {
        Toast('success', "Designer assigned succesfully");
        AssignToDesignData(currentPage)
      }
      else {
        Toast('error', res.data?.message)
      }
    } catch (error) {
      console.error(error);
    }
  }

  const handlePickClick = (row) => {
    const creative_id = row.creative_id;
    const designerSel = designerAssignments[creative_id] ? JSON.parse(designerAssignments[creative_id]) : {};
    if (!designerSel?.id) {
      Toast('warning', 'Please select a designer.');
      return;
    }
    const cwSel = contentWriterAssignments[creative_id] ? JSON.parse(contentWriterAssignments[creative_id]) : null;
    if (cwSel?.id) {
      // Ask only for description and then proceed
      setPickModalContext({
        creative_id,
        content_id: row.content_id || null,
        cw: { id: cwSel.id, name: cwSel.name },
        designer: { id: designerSel.id, name: designerSel.name }
      });
      setPickModalOpen(true);
      return;
    }
    // No content writer selected; pick ad directly
    handlePick({ creative_id, designer_id: designerSel.id, designer_name: designerSel.name });
  };

  const handleConfirmPickWithDescription = async ({ creative_id, content_id, description }) => {
    try {
      const cw = pickModalContext.cw;
      if (cw?.id) {
        await api({
          url: `/alpha_cst_designer/contentWriter`,
          method: 'POST',
          responseType: 'json',
          data: {
            content_id: content_id || null,
            creative_id,
            content_writer_id: cw.id,
            content_writer_name: cw.name,
            description: description || ''
          }
        });
      }
    } catch (err) {
      Toast('error', 'Failed to assign content writer. Proceeding to pick designer.');
    }
    const designer = pickModalContext.designer;
    await handlePick({ creative_id, designer_id: designer.id, designer_name: designer.name });
    setPickModalOpen(false);
    setPickModalContext({ creative_id: null, content_id: null, cw: null, designer: null });
  };

  return (
    <div>
      <FilterBar
        dateRange={dateRange}
        onDateChange={handleDateChange}
        showDesigner={false}
        status={status}
        onStatusChange={handleStatusChange}
        statusOptions={STATUS_OPTIONS}
        showStatus={false}
        adType={adType}
        onAdTypeChange={handleAdTypeChange}
        adTypeOptions={types}
        showAdType={true}
        search={search}
        onSearchChange={handleSearchChange}
        showSearch={true}
      />
      <ScrollContainer>
        <table className="table table-bordered">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-left">Sales Person Details</th>
              <th>Ad Creation Date</th>
              <th>Campaign Name</th>
              <th>Creative Type</th>
              <th>Location Details</th>
              <th>Assign to Designer</th>
              <th>Assign to Content Writer</th>
              <th>Action</th>
              <th>Ad Details</th>
            </tr>
          </thead>
          {assignAds?.length > 0 && !loader ? (
            assignAds.map((row, idx) => {
              const created_at = alphaformatDateTime(row.created_at);
              const start_date = alphaformatDateTime(row.creative_start_date);
              const end_date = alphaformatDateTime(row.creative_start_date,'end');
              return (
                <tbody key={row.creative_id}>
                  <tr className={`border-t ${idx %2 === 0 ? '' : 'bg-gray-100'}`}>
                    <td className="p-2">
                      <div className="flex items-center gap-1">
                        <sl-icon name="person" />
                        <span>{row.name}</span>
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <sl-icon name="telephone" />
                        <span>{row.mobile}</span>
                      </div>
                      <div>
                        {start_date.dateStr+' '+start_date.timeStr} - {end_date.dateStr+' '+end_date.timeStr}
                      </div>
                    </td>
                    <td>
                      <div className="font-bold">{created_at.dateStr}</div>
                      <div className="text-xs text-gray-500 font-bold">{created_at.timeStr}</div>
                    </td>
                    <td>
                      <div className="flex flex-col gap-1 justify-start items-start">
                        <><span className="font-semibold px-1 py-1 border-x border-blue-300"> creative : {row.creative_name}</span> <hr className="w-full border-gray-500 my-1" /></>
                        {row.brand_name && (<><span>brand name : {row.brand_name}</span> <hr className="w-full border-gray-500 my-1" /></>)}
                        {row.client_name && <span>client name : {row.client_name}</span>}
                      </div>
                    </td>
                    <td>
                      <div className="flex flex-col gap-1 justify-start items-start">
                        {row.type && <><span>{row.type}</span> <hr className="w-full border-gray-500 my-1" /></>}
                        {row.ad_creative_sub_type && <span>{row.ad_creative_sub_type}</span>}
                      </div>
                    </td>
                    <td>
                      <LocationDetails data={row}/>
                    </td>
                    <td>
                      <select
                        className="border p-1 rounded"
                        value={designerAssignments[row.creative_id] || ""}
                        onChange={(e) =>
                          setDesignerAssignments((prev) => ({
                            ...prev,
                            [row.creative_id]: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select Designer</option>
                        {Designer.map((designer) => (
                          <option key={designer.designer_id} value={JSON.stringify({ id: designer.designer_id, name: designer.name })}>
                            {designer.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="border p-1 rounded"
                        value={contentWriterAssignments[row.creative_id] || ""}
                        onChange={(e) =>
                          setContentWriterAssignments((prev) => ({
                            ...prev,
                            [row.creative_id]: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select Content Writer</option>
                        {(contentWriters || []).map((cw) => (
                          <option key={cw.value} value={JSON.stringify({ id: cw.value, name: cw.label })}>
                            {cw.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700" onClick={() => handlePickClick(row)}>
                        Pick Ad
                      </button>
                    </td>
                    <td>
                      <button className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700" onClick={()=>{setRowData(row); setShowAdDetails(true)}}>View Ad Details</button>
                    </td>
                  </tr>
                </tbody>
              );
            })
          ) : !assignAds?.length && !loader ? (
            <NoDataFound colspan="12" />
          ) : (
            <TableLoader colspan="12" />
          )}
        </table>
      </ScrollContainer>
      <div className="flex justify-end">{totalPages > 1 && <Paginator pageCount={totalPages} handlePageClick={handlePageClick} forcePage={currentPage} />}</div>

      <ContentWriterAssignmentModal
        isOpen={pickModalOpen}
        onClose={() => {
          setPickModalOpen(false);
          setPickModalContext({ creative_id: null, content_id: null, cw: null, designer: null });
        }}
        onConfirm={handleConfirmPickWithDescription}
        contentWriters={[]}
        currentContentWriter={pickModalContext.cw ? { content_writer_id: pickModalContext.cw.id, name: pickModalContext.cw.name } : null}
        currentDescription={""}
        creativeId={pickModalContext.creative_id}
        content_id={pickModalContext.content_id}
        hideWriterSelect={true}
      />
      <ContentDrawer isOpen={showAdDetails} type="adDetails" rowData={rowData} onClose={()=>setShowAdDetails(!showAdDetails)} />
    </div>
  );
};

export default AssignToDesignerAds;